import { KeyboardInput } from "@cervval.js/client-html5-core/src/tools/dom.tools";
import { LogService } from "@cervval.js/common/services";
import { setProp } from "@cervval.js/common/src/tools/any.tools";
import { removeFirstFromArray, wrapAsArray } from "@cervval.js/common/src/tools/collection.array.tools";
import { fromPx, toPx } from "@cervval.js/common/src/tools/number.tools";
import { clone } from "@cervval.js/common/src/tools/object.tools";
import { DEFAULT_DATETIME_FORMAT, format, pxToTimestampY, timestampToPxY, UnixTimestamp, WorkDateType } from "@cervval.js/common/src/tools/timestamps.tools";
import fastdom from "fastdom";
import { CjsDialog, DialogPosition, DialogPreConfig, DialogSize, showErrorInDialog } from "../../../dialog/dialog";
import { CjsDayAgendaColumnView } from "../dayAgendaColumnView/dayAgendaColumnView";
import { CjsDayAgendaEditItemForm } from "../dayAgendaEditItemForm/dayAgendaEditItemForm";
import { CjsDayAgenda } from "./dayAgenda";
import { ColumnUid } from "./dayAgenda.types";
import { buildColContextItems, magnet } from "./dayAgendaComputation.helper";
import { ColumnContext, DayAgendaContext } from "./dayAgendaContext";
import { DayAgendaRefreshReason, ItemInteraction } from "./dayAgendaEvents";
import { DayAgendaModel, PositionProperties } from "./dayAgendaModel";
import { DayAgendaOptions } from "./dayAgendaOptions";
import { removeColumnViewsHoverStyle } from "./dayAgendaUi.helper";
import { COLUMN_HOVERED_CLASS, COLUMN_HOVERED_NOT_ALLOWED_CLASS, COLUMN_ITEM_BOTTOM_HANDLE_CLASS, COLUMN_ITEM_CLASS, COLUMN_ITEM_HANDLE_CLASS, COLUMN_ITEM_SELECTED_CLASS, COLUMN_ITEM_TOP_HANDLE_CLASS, DATA_ITEM_UID_ATTRIBUTE } from "./dayAgendaUi.helper.consts";

/** Agenda col view container ref */
export const AGENDA_COLUMN_VIEW_CONTAINER_REF = "dayAgendaColumnViewContainer";

/** Box shadow color for drag/resize */
const boxShadowColor = "#00000070";

/** Box shadow X offset for resize, in px */
const boxShadowOffsetX = 4;

/** Box shadow Y offset for resize, in px */
const boxShadowOffsetY = 0;

/** Box shadow blur radius for resize, in px*/
const boxShadowBlurRadius = 5;

/** Box shadow spread radius for resize, in px */
const boxShadowSpreadRadius = -5;

const boxShadowDrag = `0 0 0.75rem ${boxShadowColor}`;

const ITEM_DRAGGED_CSS_CLASS = "item-dragged";

/** Minimum cursor Y distance before triggering the item drag event (in px) */
const MIN_MOVE_DISTANCE = 3;

/** Type of handle (used to resize an item) */
export type HandleType = "top" | "bottom";

/**
 * Install items interaction such as drag/drop and resize on a agenda
 * @param agenda Agenda
 * @param agendaModel Agenda model
 */
export function installItemsInteractions<
    ItemObject,
    ColumnObject
>(
    agenda: CjsDayAgenda<ItemObject, ColumnObject>,
    agendaModel: DayAgendaModel<ItemObject, ColumnObject>,
    log?: LogService
): void {

    const logService = log ?? console;
    const agendaContext = agenda.getAgendaContext();
    const agendaOptions = agenda.options;

    agenda.selectionManager.onAdd = (selectedData) => {
        for (const sd of selectedData) {
            sd.data = { oldStyle: clone(sd.htmlItem.style) };
            if (agendaOptions.defaultColOptions.defaultItemStyle.selectedBackground != null) {
                sd.htmlItem.style.background = agendaOptions.defaultColOptions.defaultItemStyle.selectedBackground;
            }
            if (agendaOptions.defaultColOptions.defaultItemStyle.selectedBorder != null) {
                sd.htmlItem.style.borderColor = agendaOptions.defaultColOptions.defaultItemStyle.selectedBorder;
            }
            sd.htmlItem.classList.add(COLUMN_ITEM_SELECTED_CLASS);
        }
    };

    agenda.selectionManager.onRemove = (selectedData) => {
        for (const sd of selectedData) {
            resetHtmlItemStyle(sd.htmlItem, sd.data.oldStyle, ["background", "borderColor"]);
            sd.htmlItem.classList.remove(COLUMN_ITEM_SELECTED_CLASS);
        }
    };

    agenda.selectionManager.onSelectionReset = () => {
        agenda.itemSelectionResetEvent.emit();
    };

    agenda.selectionManager.getItemUid = agendaModel.getItemUid.bind(agendaModel);

    // On delete key down => delete item selection
    bindDeleteItemEvent(agenda, agendaModel, agendaContext, agendaOptions);

    // create interaction if no item under the mouse or drag interaction if there is an item under the mouse
    agenda.components.columnsContainer.addEventListener("mousedown", (e) => {
        const isLeftClick = e.button === 0;
        const isRightClick = e.button === 2;

        if (!isLeftClick && !isRightClick) { return; } // We only want to perform interaction on left or right click, cf https://developer.mozilla.org/en-US/docs/Web/API/MouseEvent/button

        if (!agendaModel) { logService.error("Unable to retrieve the agenda model"); return; }

        // Retrieve html item and columns elements
        const htmlItem: HTMLElement | undefined = findHtmlItemByClassFromEvent(e, COLUMN_ITEM_CLASS);
        const colView = findColumnViewFromEvent<ItemObject, ColumnObject>(e);

        if (!colView?.columnContext) { return; }

        // Retrieve agenda attributes
        const colContainerX = agenda.getAgendaColumnsContainerX();
        const colContainerY = agenda.getAgendaColumnsContainerY();

        // ensure moveDragEventMainDirection is reset
        moveDragEventMainDirection = null;

        // Mouse down on a agenda column
        const mouseDownTs: UnixTimestamp = pxToTimestampY(colView.columnContext, { px: e.y - colContainerY, targetDateType: WorkDateType.START });

        // Find the corresponding ColumnObject
        const colUid = colView.columnContext?.uid;

        if (!colUid) { logService.error("Unable to retrieve column data uid"); return; }

        if (agendaModel.isEditableOnTs && !agendaModel.isEditableOnTs(mouseDownTs)) {
            // Agenda is not editable on the targeted mouse down timestamp
            return;
        }


        if (htmlItem && isLeftClick) { // Mouse down on a agenda item

            // Find the corresponding ItemObject
            const itemDataUid: string | null = htmlItem.getAttribute(DATA_ITEM_UID_ATTRIBUTE);
            if (!itemDataUid) { logService.error("Unable to retrieve item data uid"); return; }

            const columnData = colView.columnContext.column;
            if (!columnData) { logService.error("Unable to retrieve column data of columnView"); return; }

            const itemObject: ItemObject | undefined = agenda.findItemByUid(agenda.getColumnItems(colUid, columnData), itemDataUid);
            if (!itemObject) { logService.error("Unable to retrieve targeted item"); return; }

            agenda.columnClickEvent.emit({
                clickPositionY: e.y - colContainerY,
                agendaColumnView: colView,
                item: itemObject,
                ts: mouseDownTs,
                originalClickEvent: e
            });

            agenda.columnContainerMouseDownEvent.emit({
                clickPositionY: e.y - colContainerY,
                ts: mouseDownTs,
                agendaColumnView: colView,
                originalClickEvent: e
            });

            // Check permission to select the item
            if (agendaModel.canSelectItem && !agendaModel.canSelectItem(itemObject)) { return; }

            agenda.selectionManager.setSelectedData([{ item: itemObject, htmlItem }], { event: e, removeFromSelectionIfAlreadySelected: true });

            // Check permission to move item
            if (agendaModel.canMoveItem(itemObject)) {

                // Store old item attributes if a reset is needed
                const oldItemStyle = clone(htmlItem.style);
                const oldTop = parseFloat(oldItemStyle.top);
                const oldHeight = parseFloat(oldItemStyle.height);

                // Init new item properties
                const newProperties: PositionProperties = {
                    // Init item's new timestamps
                    startTs: agendaModel.getItemStartTs(itemObject),
                    finishTs: agendaModel.getItemFinishTs(itemObject),

                    // Init new attributes : position and height
                    pxX: parseFloat(oldItemStyle.left),
                    pxY: oldTop,
                    height: oldHeight,
                    hasBeenMoved: false
                };

                // Ensure that we can see the resizing of the item
                htmlItem.style.zIndex = String(parseFloat(oldItemStyle.zIndex) + 1);

                // Retrieve HTML Element's handle information and init resize events if needed
                const htmlTarget = getEventHtmlElementTarget(e);
                if (isHtmlElementHandle(htmlTarget)) {

                    if (agendaModel?.canRescheduleItem(itemObject, newProperties.startTs, newProperties.finishTs)) {
                        // Retrieve the handle type
                        const handleType = getHandleType(htmlTarget);
                        if (handleType === null) { logService.error("Handle type is not valid"); return; }

                        // Set handle css style
                        htmlItem.style.boxShadow = getBoxShadowStyleForHandle(handleType);

                        agenda.selectionManager.setSelectedData([{ item: itemObject, htmlItem }]);

                        // On move event => resizing of the item
                        const onMouseMove = bindResizeDragEvent(htmlItem, colView, itemObject, colContainerY, handleType, oldHeight, agenda, agendaOptions, newProperties);

                        // On mouse up event => drop handle => end of item resizing
                        bindResizeDropEvent(htmlItem, colView, itemObject, handleType, oldItemStyle, agenda, agendaContext, agendaModel, agendaOptions, newProperties, onMouseMove);
                    }

                } else { // Drag item event (move)

                    // Dispatch click event
                    agenda.itemClickEvent.emit({
                        itemTop: oldItemStyle.top,
                        itemHeight: oldItemStyle.height,
                        startTs: newProperties.startTs,
                        finishTs: newProperties.finishTs,
                        colUid: colUid,
                        item: itemObject,
                        htmlItem,
                        originalClickEvent: e
                    });

                    const hoverItem = {
                        columnHovered: colView,
                        oldColumnHoveredStyle: clone(colView.style)
                    };

                    // On mouse move event => item is dragged
                    const onMouseMove = bindMoveDragEvent(htmlItem, colView, itemObject, colContainerX, colContainerY, e, agenda, agendaModel, agendaContext, agendaOptions, newProperties, hoverItem, oldItemStyle);

                    // On mouse up event => item is dropped
                    bindMoveDropEvent(htmlItem, colView, itemObject, oldItemStyle, agenda, agendaModel, agendaContext, agendaOptions, newProperties, hoverItem, colUid, onMouseMove, colContainerY);
                }
            }
        } else if (isLeftClick && !agendaOptions.createItemsOnRightClick || isRightClick && agendaOptions.createItemsOnRightClick) { // Item creation, with left click or right click if 'createItemsOnRightClick' is true
            agenda.columnClickEvent.emit({
                clickPositionY: e.y - colContainerY,
                agendaColumnView: colView,
                ts: mouseDownTs,
                originalClickEvent: e
            });
            agenda.selectionManager.resetSelectedData();

            if (colView.columnContext?.column && agendaModel.canCreateItemOnColumn(colView.columnContext.column as ColumnObject, mouseDownTs)) {
                // Store old item attributes if a reset is needed
                const oldHeight = 0;
                let oldTop = e.y - colContainerY;
                let startTsOrigin = mouseDownTs;
                startTsOrigin = magnet(startTsOrigin, null, colView.columnContext.column as ColumnObject, agendaOptions.magnetismOptions || {});
                oldTop = timestampToPxY(colView.columnContext, { ts: startTsOrigin });

                // Init new item properties
                const newProperties: PositionProperties = {
                    // Init item's new timestamps
                    startTs: startTsOrigin,
                    finishTs: startTsOrigin,

                    // Init new attributes : position and width
                    pxX: 0,
                    pxY: oldTop,
                    height: oldHeight,
                    hasBeenMoved: false
                };

                bindCreationClickEvent(newProperties, colView, e, agenda, agendaModel, agendaContext, agendaOptions);
            }
        }
    });

    agenda.components.columnsContainer.addEventListener("dblclick", (e) => {
        if (!agendaModel) { logService.error("Unable to retrieve the agenda model"); return; }

        // Retrieve html item and columns elements
        const htmlItem: HTMLElement | undefined = findHtmlItemByClassFromEvent(e, COLUMN_ITEM_CLASS);
        const colView = findColumnViewFromEvent<ItemObject, ColumnObject>(e);

        if (!colView?.columnContext || !htmlItem) { return; }

        // Find the corresponding ColumnObject
        const colUid = colView.columnContext?.uid;

        // Find the corresponding ItemObject
        const itemDataUid: string | null = htmlItem.getAttribute(DATA_ITEM_UID_ATTRIBUTE);
        if (!itemDataUid) { logService.error("Unable to retrieve item data uid"); return; }

        const columnData = colView.columnContext.column;
        if (!columnData) { logService.error("Unable to retrieve column data of columnView"); return; }

        const itemObject: ItemObject | undefined = agenda.findItemByUid(agenda.getColumnItems(colUid, columnData), itemDataUid);
        if (!itemObject) { logService.error("Unable to retrieve targeted item"); return; }

        bindDoubleClickEvent(itemObject, htmlItem, colView, agenda, agendaModel, agendaContext, agenda.options);
    });
}

/**
 * Find an HTMLElement by class from an event's targets
 * @param e Event
 * @param itemClass Css class of the element we need to find
 * @returns HTML element found or undefined
 */
export function findHtmlItemByClassFromEvent(e: Pick<Event, "composedPath">, itemClass: string): HTMLElement | undefined {
    return e.composedPath().find(elt => (elt as HTMLElement)?.classList?.contains(itemClass)) as HTMLElement;
}

/**
 * Find a targeted col from an event
 * @param e Event
 * @returns HTML element col or undefined
 */
export function findColumnViewFromEvent<ItemObject, ColumnObject>(e: Pick<Event, "composedPath">): CjsDayAgendaColumnView<ItemObject, ColumnObject> | undefined {
    return e.composedPath().find((elt) => {
        return (elt as HTMLElement)?.classList ? (elt as HTMLElement)?.classList.contains(CjsDayAgendaColumnView.TAG_NAME) : false;
    }) as CjsDayAgendaColumnView<ItemObject, ColumnObject>;
}

/**
 * Return the target of an event as an HTMLElement
 * @param e Event
 * @returns HTMLElement event target
 */
function getEventHtmlElementTarget(e: Event): HTMLElement {
    return e.target as HTMLElement;
}

/**
 * Reset the css style properties of an HTMLElement
 * @param htmlItem HTMLElement
 * @param oldStyle Saved old style
 * @param cssProperties Properties of the old style to re-apply
 */
function resetHtmlItemStyle(htmlItem: HTMLElement, oldStyle: CSSStyleDeclaration, cssProperties: (keyof CSSStyleDeclaration)[]): void {
    if (htmlItem == null) { return; }

    for (const ppt of cssProperties) {
        if (typeof ppt === "number") { continue; }
        setProp(htmlItem.style, ppt, oldStyle[ppt]);
    }
}

/**
 * Return the handle box-shadow style for its specific type
 * @param handleType Type of the handle
 * @returns box-shadow style string
 */
function getBoxShadowStyleForHandle(handleType: HandleType): string {
    const factorHandleType = getHandleDirectionFactor(handleType);
    return `${boxShadowColor} ${boxShadowOffsetX * factorHandleType}px ${boxShadowOffsetY}px ${boxShadowBlurRadius}px ${boxShadowSpreadRadius}px`;
}

/**
 * Check if an HTMLElement is a handle (used for resize events)
 * @param htmlElem HTMLElement to test
 * @returns True is the HTMLElement is a handle. False otherwise
 */
function isHtmlElementHandle(htmlElem: HTMLElement): boolean {
    return htmlElem?.classList.contains(COLUMN_ITEM_HANDLE_CLASS) ?? false;
}

/**
 * Get the type of handle of an HTMLElement
 * @param htmlElem HTMLElement handle
 * @returns Handle type is found, null otherwise.
 */
function getHandleType(htmlElem: HTMLElement): HandleType | null {
    if (htmlElem.classList.contains(COLUMN_ITEM_TOP_HANDLE_CLASS)) { return "top"; }
    else if (htmlElem.classList.contains(COLUMN_ITEM_BOTTOM_HANDLE_CLASS)) { return "bottom"; }
    return null;
}

/**
 * Get a direction factor for a handleType. Used to compute elements positions.
 * @param handleType Type of handle
 * @returns factor as a number
 */
function getHandleDirectionFactor(handleType: HandleType): number {
    if (handleType === "top") { return -1; }
    else if (handleType === "bottom") { return 1; }
    else { return 1; }
}

/**
 * Update col contexts like col height or its items' context
 * @param colContexts Col contexts to update
 * @param agendaContext Agenda context
 * @param agenda Agenda
 * @param agendaModel Agenda model
 * @param agendaOptions Agenda options
 */
export function updateColContext<
    ItemObject,
    ColumnObject
>(
    colContexts: ColumnContext<ItemObject, ColumnObject>[],
    agendaContext: DayAgendaContext,
    agenda: CjsDayAgenda<ItemObject, ColumnObject>,
    agendaModel: DayAgendaModel<ItemObject, ColumnObject>,
    agendaOptions: DayAgendaOptions<ItemObject, ColumnObject>
): void {

    for (const colContext of colContexts) {
        const colContextItems = buildColContextItems(agendaContext, colContext, agenda.getColumnItems(colContext.uid, colContext.column), agendaModel, agendaOptions.defaultColOptions, agenda.selectionManager);
        colContext.itemContexts = colContextItems;
    }
}

/**
 * Update an item and related cols.
 */
function updateItemAndColumns<
    ItemObject,
    ColumnObject
>(
    colContexts: ColumnContext<ItemObject, ColumnObject>[],
    agendaContext: DayAgendaContext,
    agenda: CjsDayAgenda<ItemObject, ColumnObject>,
    agendaModel: DayAgendaModel<ItemObject, ColumnObject>,
    agendaOptions: DayAgendaOptions<ItemObject, ColumnObject>,
    refreshReason: DayAgendaRefreshReason
): void {
    updateColContext(colContexts, agendaContext, agenda, agendaModel, agendaOptions);
    // Refresh agenda to display new cols or delete undisplayed cols
    agenda.refresh(refreshReason);
}

/** Bind resize drag event of the item */
function bindResizeDragEvent<
    ItemObject,
    ColumnObject
>(
    htmlItem: HTMLElement,
    colView: CjsDayAgendaColumnView<ItemObject, ColumnObject>,
    itemObject: ItemObject,
    colContainerY: number,
    handleType: HandleType,
    oldHeight: number,
    agenda: CjsDayAgenda<ItemObject, ColumnObject>,
    agendaOptions: DayAgendaOptions<ItemObject, ColumnObject>,
    newProperties: PositionProperties
): (event: MouseEvent) => void {
    let lastPropertiesPxY = newProperties.pxX;

    const onMouseMove = (event: MouseEvent): void => {
        const colContext = colView.columnContext;
        if (!colContext) { return; }

        event.preventDefault();
        // Compute the distance corresponding to the resize amount
        const topParsed = parseFloat(htmlItem.style.top);
        const heightParsed = parseFloat(htmlItem.style.height);
        const distY = event.y - (topParsed + colContainerY);
        newProperties.pxY = topParsed + distY;
        let tmpTs = pxToTimestampY(colContext, { px: newProperties.pxY, targetDateType: handleType === "top" ? WorkDateType.START : WorkDateType.FINISH });
        let newStartTs = newProperties.startTs;
        let newFinishTs = newProperties.finishTs;

        // Apply magnetism if needed
        if (handleType === "top") {
            tmpTs = magnet(tmpTs, itemObject, colView.columnContext?.column as ColumnObject, agendaOptions.magnetismOptions || {}, { adjustOnlyStartTs: true });
            newStartTs = tmpTs;
            newProperties.pxY = timestampToPxY(colContext, { ts: tmpTs });

            if (newProperties.pxY >= Math.floor(topParsed + heightParsed)) {
                newProperties.pxY = topParsed;
            }
            newProperties.height = heightParsed - newProperties.pxY + topParsed;
        } else if (handleType === "bottom") {
            tmpTs = magnet(tmpTs, itemObject, colView.columnContext?.column as ColumnObject, agendaOptions.magnetismOptions || {}, { adjustOnlyFinishTs: true });
            newFinishTs = tmpTs;
            newProperties.pxY = timestampToPxY(colContext, { ts: tmpTs });
            if (newProperties.pxY < Math.ceil(topParsed)) {
                newProperties.pxY = topParsed + heightParsed;
            }
            newProperties.height = newProperties.pxY - topParsed;
        }

        // Set new item properties
        if (handleType === "top") {
            htmlItem.style.top = toPx(newProperties.pxY);
        }

        htmlItem.style.height = toPx(newProperties.height < 0 ? oldHeight : newProperties.height);

        if (newProperties.pxY !== lastPropertiesPxY) {
            // Dispatch resize drag event on each resize step
            lastPropertiesPxY = newProperties.pxY;
            agenda.itemResizeDragEvent.emit({
                handleType: handleType,
                oldItemHeight: oldHeight,
                newItemHeight: newProperties.height,
                newStartTs: newStartTs,
                newFinishTs: newFinishTs,
                item: itemObject,
                htmlItem: htmlItem
            });
        }
    };
    document.body.addEventListener("mousemove", onMouseMove);
    return onMouseMove;// mousemove will be removed on mouse up
}

/**
 * @returns formatted item names if agenda.options.logMessage.getItemName is defined, the item count string otherwise
 */
function formatItems<
    ItemObject,
    ColumnObject
>(items: ItemObject[], agenda: CjsDayAgenda<ItemObject, ColumnObject>): string {
    if (agenda.options.logMessage?.getItemName) {
        const getItemName = agenda.options.logMessage?.getItemName;
        return items.map(i => getItemName(i)).join(", ");
    }
    return items.length + " " + (items.length > 1 ? agenda.i18n.logMessage.items : agenda.i18n.logMessage.item);
}

/** Bind resize drop event of the item */
function bindResizeDropEvent<
    ItemObject,
    ColumnObject
>(
    htmlItem: HTMLElement,
    colView: CjsDayAgendaColumnView<ItemObject, ColumnObject>,
    itemObject: ItemObject,
    handleType: HandleType,
    oldItemStyle: CSSStyleDeclaration,
    agenda: CjsDayAgenda<ItemObject, ColumnObject>,
    agendaContext: DayAgendaContext,
    agendaModel: DayAgendaModel<ItemObject, ColumnObject>,
    agendaOptions: DayAgendaOptions<ItemObject, ColumnObject>,
    newProperties: PositionProperties,
    /** onMouseMove was added to body at mouse down and needs to be removed at mouse up */
    onMouseMove: (event: MouseEvent) => void
): void {
    document.body.addEventListener("mouseup", (event: MouseEvent) => {

        const colContext = colView.columnContext;
        if (!colContext) { return; }

        // Update the handle's corresponding timestamp
        if (handleType === "top") {
            newProperties.startTs = pxToTimestampY(colContext, { px: newProperties.pxY, targetDateType: WorkDateType.START });
        } else if (handleType === "bottom" && newProperties.pxY > parseFloat(oldItemStyle.top)) {
            newProperties.finishTs = pxToTimestampY(colContext, { px: newProperties.pxY, targetDateType: WorkDateType.FINISH });
        }

        // build logMessage
        let logMessage: string | undefined;
        if (agenda.options.logMessage?.formatMessage) {
            logMessage = agenda.options.logMessage.formatMessage([itemObject], handleType === "top" ? "resize_start" : "resize_finish");
        }

        if (logMessage == null) {
            if (handleType === "top") {
                logMessage = agenda.i18n.logMessage.resizeStartAgendaItem({ items: formatItems([itemObject], agenda), target: format(newProperties.startTs, DEFAULT_DATETIME_FORMAT) });
            } else {
                logMessage = agenda.i18n.logMessage.resizeFinishAgendaItem({ items: formatItems([itemObject], agenda), target: format(newProperties.finishTs, DEFAULT_DATETIME_FORMAT) });
            }
        }

        // Check permission on the item's rescheduling on the new timestamps
        let resetItem = true;
        agendaModel.encapsulateInAction(async (action) => {
            if (agendaModel?.canRescheduleItem(itemObject, newProperties.startTs, newProperties.finishTs)) {
                let processResize = true;
                if (agendaModel.resizeItem) {
                    const resizeResult = await agendaModel.resizeItem({ itemObject, handleType, newProperties }, action);
                    if (resizeResult !== "continue_with_standard_process") {
                        processResize = false;
                    }
                }
                // Set item's new timestamps
                if (processResize) {
                    if (handleType === "top") {
                        agendaModel.setItemStartTs(itemObject, newProperties.startTs, action, ItemInteraction.RESIZE);
                    } else if (handleType === "bottom") {
                        agendaModel.setItemFinishTs(itemObject, newProperties.finishTs, action, ItemInteraction.RESIZE);
                    }

                    // Called afterResizeProcess if defined
                    if (agendaModel.afterResizeItemProcess) {
                        await agendaModel.afterResizeItemProcess?.({ itemObject, action });
                    }
                }

                // Item's position won't be reset
                resetItem = false;
            }
            return action.operations.length;
        }, {
            logMessage: logMessage ?? "" // can't be undefined
        }).then((operationsCount: number) => {

            // if app will refresh later, we don't refresh the agenda items
            if (!agendaOptions.appWillRefreshAgendaAfterAction || operationsCount === 0) {
                // Update col context if needed
                const colContextsToUpdate: ColumnContext<ItemObject, ColumnObject>[] = [];
                if (colView.columnContext && agendaModel) {
                    colContextsToUpdate.push(colView.columnContext);
                }
                // Update item
                updateItemAndColumns(colContextsToUpdate, agendaContext, agenda, agendaModel, agendaOptions, DayAgendaRefreshReason.ITEM_RESIZE);

                // Reset style properties if needed (item dropped and/or invalid position)
                const styleToReset: (keyof CSSStyleDeclaration)[] = ["zIndex", "boxShadow", "cursor"];
                if (resetItem) {
                    styleToReset.push("top", "height");
                }
                resetHtmlItemStyle(htmlItem, oldItemStyle, styleToReset);
            }

            // Dispatch resize event
            agenda.afterItemResizeEvent.emit({
                handleType,
                oldItemTop: oldItemStyle.top,
                oldItemHeight: oldItemStyle.height,
                newItemTop: htmlItem.style.top,
                newItemHeight: htmlItem.style.height,
                newStartTs: newProperties.startTs,
                newFinishTs: newProperties.finishTs,
                item: itemObject,
                htmlItem
            });

            // Dispatch item interaction finish event
            agenda.itemInteractionFinishEvent.emit({ event: ItemInteraction.RESIZE });
        }).catch(showErrorInDialog);

        // Remove mouse move event listener
        document.body.removeEventListener("mousemove", onMouseMove);
    }, { once: true });
}

const moveDragEventMainDirectionTrigger = 20;// In px
let moveDragEventMainDirection: "x" | "y" | null = null;

/** Bind move drag event of the item */
function bindMoveDragEvent<
    ItemObject,
    ColumnObject
>(
    htmlItem: HTMLElement,
    colView: CjsDayAgendaColumnView<ItemObject, ColumnObject>,
    itemObject: ItemObject,
    colContainerX: number,
    colContainerY: number,
    mouseDownEvent: MouseEvent,
    agenda: CjsDayAgenda<ItemObject, ColumnObject>,
    agendaModel: DayAgendaModel<ItemObject, ColumnObject>,
    agendaContext: DayAgendaContext,
    agendaOptions: DayAgendaOptions<ItemObject, ColumnObject>,
    newProperties: PositionProperties,
    hoverItem: {
        columnHovered: CjsDayAgendaColumnView<ItemObject, ColumnObject>,
        oldColumnHoveredStyle: CSSStyleDeclaration
    },
    oldItemStyle: CSSStyleDeclaration
): (event: MouseEvent) => void {

    const deltaX = mouseDownEvent.clientX - htmlItem.getBoundingClientRect().x;
    const deltaY = mouseDownEvent.clientY - (fromPx(htmlItem.style.top) + colContainerY);

    // Store last mouse move x,y event,used for oneAxis drag option
    let lastMouseMoveX = mouseDownEvent.clientX;
    let lastMouseMoveY = mouseDownEvent.clientY;

    // On first mouse move only, move htmlItem from colView to colsContainer
    let isFirstMove = true;

    const onMouseMove = (event: MouseEvent): void => {
        event.preventDefault();

        // Compute the horizontal and vertical distance
        const moveX = event.clientX - lastMouseMoveX;
        const moveY = event.clientY - lastMouseMoveY;

        // Check if a move was made by mouse
        if (Math.abs(moveX + moveY) < MIN_MOVE_DISTANCE) { return; }


        if (isFirstMove) {
            const itemTop = fromPx(htmlItem.style.top);

            // Always fix width of item
            htmlItem.style.width = toPx(htmlItem.clientWidth);

            // Always fix width of item
            htmlItem.style.left = toPx(colView.offsetLeft);

            // Fix new item top, parent has changed, so top should be updated
            htmlItem.style.top = toPx(colView.getBoundingClientRect().y + itemTop - colContainerY);

            // Append item to colContainer => avoid item deletion during agenda refresh when auto-scrolling
            agenda.components.columnsContainer.append(htmlItem);

            isFirstMove = false;
        }

        const distX = event.clientX - colContainerX;
        const distY = event.clientY - colContainerY;

        const dragXEnable = isDragXEnable<ItemObject, ColumnObject>({
            agendaOptions,
            agendaModel,
            itemObject,
            newColView: colView
        });

        const dragYEnable = isDragYEnable<ItemObject, ColumnObject>({
            agendaOptions,
            itemObject,
            agendaModel,
            newStartTs: newProperties.startTs,
            newFinishTs: newProperties.finishTs
        });

        const eventDeltaX = event.clientX - lastMouseMoveX;
        const eventDeltaY = event.clientY - lastMouseMoveY;

        // Check if user move more in a direction than an other
        const dragOneAxisTrigger = Math.abs(eventDeltaX) > moveDragEventMainDirectionTrigger || Math.abs(eventDeltaY) > moveDragEventMainDirectionTrigger;

        // If dragOneAxis and dragMainDirection not already set
        if (isDragOneAxisEnable(agendaOptions) && dragOneAxisTrigger && !moveDragEventMainDirection) {

            // Set the main drag direction
            moveDragEventMainDirection = Math.abs(eventDeltaX) > Math.abs(eventDeltaY) ? "x" : "y";

            lastMouseMoveX = event.clientX;// Update x
            lastMouseMoveY = event.clientY;// Update y
        }

        let tmpStartTs: UnixTimestamp | undefined;

        // -- X --
        if (dragXEnable) {

            // Update the item's top property
            newProperties.pxX = distX - deltaX;
            htmlItem.style.left = toPx(newProperties.pxX);

            // Highlight col on hover if needed
            highlightColumnIfNeeded({
                htmlItem,
                agenda,
                itemObject,
                hoverItem,
                agendaModel,
                agendaOptions,
                oldColView: colView,
                newStartTs: newProperties.startTs,
                newFinishTs: newProperties.finishTs,
                event
            });
        }

        // -- Y --
        if (dragYEnable && colView.columnContext?.column) {
            newProperties.pxY = distY - deltaY;
            tmpStartTs = pxToTimestampY(colView.columnContext, { px: newProperties.pxY, targetDateType: WorkDateType.START });

            // Apply magnetism if needed
            tmpStartTs = magnet(tmpStartTs, itemObject, colView.columnContext.column, agendaOptions.magnetismOptions || {});

            newProperties.pxY = timestampToPxY(colView.columnContext, { ts: tmpStartTs });
            htmlItem.style.top = toPx(newProperties.pxY);

        }

        if (!newProperties.hasBeenMoved) {
            // Set item dragging style
            htmlItem.style.boxShadow = boxShadowDrag;
            htmlItem.style.cursor = "grabbing";
            htmlItem.classList.add(ITEM_DRAGGED_CSS_CLASS);

            // Dispatch dragging event
            agenda.itemDragEvent.emit({
                itemTop: oldItemStyle.top,
                itemHeight: oldItemStyle.height,
                startTs: newProperties.startTs,
                finishTs: newProperties.finishTs,
                columnUid: colView.columnContext?.uid ?? "",
                item: itemObject,
                htmlItem
            });
            newProperties.hasBeenMoved = true;
        }

        // Dispatch currently dragging event
        agenda.itemOnDragEvent.emit({
            itemTop: oldItemStyle.top,
            itemHeight: oldItemStyle.height,
            startTs: tmpStartTs ?? newProperties.startTs,
            finishTs: newProperties.finishTs,
            columnUid: colView.columnContext?.uid ?? "",
            item: itemObject,
            htmlItem
        });

    };

    document.body.addEventListener("mousemove", onMouseMove);
    return onMouseMove;// mousemove will be removed on mouse up
}

/** Bind move drop event of the item */
function bindMoveDropEvent<
    ItemObject,
    ColumnObject
>(
    htmlItem: HTMLElement,
    oldColView: CjsDayAgendaColumnView<ItemObject, ColumnObject>,
    itemObject: ItemObject,
    oldItemStyle: CSSStyleDeclaration,
    agenda: CjsDayAgenda<ItemObject, ColumnObject>,
    agendaModel: DayAgendaModel<ItemObject, ColumnObject>,
    agendaContext: DayAgendaContext,
    agendaOptions: DayAgendaOptions<ItemObject, ColumnObject>,
    newProperties: PositionProperties,
    hoverItem: {
        columnHovered: CjsDayAgendaColumnView<ItemObject, ColumnObject>,
        oldColumnHoveredStyle: CSSStyleDeclaration
    },
    colDataUid: string,// initial col Uid
    /** onMouseMove was added to body at mouse down and needs to be removed at mouse up */
    onMouseMove: (event: MouseEvent) => void,
    colContainerY: number
): void {

    const resetMoveDropItem = (colView: CjsDayAgendaColumnView<ItemObject, ColumnObject>): void => {
        fastdom.mutate(() => {
            // ! Don't forget to reset item parent, even if item hasn't been moved
            // Replace item in col if item was moved (it was in colsContainer during drag)
            resetHtmlItemStyle(htmlItem, oldItemStyle, ["zIndex", "boxShadow", "cursor"]);
            resetHtmlItemStyle(htmlItem, oldItemStyle, ["top", "left", "width", "background"]);
        });
        // Remove mouse move event
        document.body.removeEventListener("mousemove", onMouseMove);
        agenda.isDraggingItem = false;
    };

    const onMoveDropListenerObject: EventListenerObject = {
        handleEvent: (event: MouseEvent): void => {

            // Reset item's dragging style and col hover style
            resetHtmlItemStyle(htmlItem, oldItemStyle, ["zIndex", "boxShadow", "cursor"]);
            resetHtmlItemStyle(hoverItem.columnHovered, hoverItem.oldColumnHoveredStyle, ["backgroundColor"]);

            const oldColData = oldColView.columnContext?.column;
            const oldColContext = oldColView.columnContext;

            // If item hasn't been moved or col not found, reset drop
            if (!newProperties.hasBeenMoved || !oldColContext || !oldColData) {
                resetMoveDropItem(oldColView);
                return;
            }

            // Object used to determine which operation to execute in encapsulateInAction
            const updatesToDo = {
                setItemColUids: false,
                setTimestamps: false
            };

            const colContextsToUpdate: ColumnContext<ItemObject, ColumnObject>[] = [];
            let itemColumnUids: ColumnUid[] | undefined;

            // Retrieve HTML col element the item is dropped to
            const elementsMouseIsOver = document.elementsFromPoint(event.clientX, event.clientY);
            let newColView = elementsMouseIsOver.find(elem => elem.classList.contains(CjsDayAgendaColumnView.TAG_NAME)) as CjsDayAgendaColumnView<ItemObject, ColumnObject> | undefined;
            let newColUid = newColView?.columnContext?.uid;

            // Reset if colView, html or colUid not found
            if (!newColView?.columnContext || !newColUid) {
                resetMoveDropItem(oldColView);
                return;
            }

            // If dragY is enable and dragX disable, newColView can only be oldColView
            if (
                isDragYEnable<ItemObject, ColumnObject>({
                    agendaOptions,
                    agendaModel,
                    itemObject,
                    newStartTs: newProperties.startTs,
                    newFinishTs: newProperties.finishTs
                })
                &&
                !isDragXEnable<ItemObject, ColumnObject>({
                    agendaOptions,
                    agendaModel,
                    itemObject,
                    newColView
                })
            ) {
                newColView = oldColView;
                newColUid = oldColView.columnContext?.uid;
            }

            const newColContext = newColView.columnContext;
            // Reset if new colCOntext don't exist
            if (!newColContext || !newColUid) {
                resetMoveDropItem(oldColView);
                return;
            }

            // If the destination col is different from the original col
            const colHasChanged = newColView !== oldColView;

            const tsDrop = pxToTimestampY(newColContext, { px: event.y - colContainerY, targetDateType: WorkDateType.START });
            // Dispatch mouseUpEvent
            agenda.columnMouseUpEvent.emit({
                mouseUpPositionY: event.y - colContainerY,
                agendaColumnView: newColView,
                ts: tsDrop,
                originalClickEvent: event,
                htmlItem,
                item: itemObject
            });

            if (// Both dragY and dragY will update start and finish ts
                isDragYEnable<ItemObject, ColumnObject>({
                    agendaOptions,
                    agendaModel,
                    itemObject,
                    newStartTs: newProperties.startTs,
                    newFinishTs: newProperties.finishTs
                }) ||
                isDragXEnable<ItemObject, ColumnObject>({
                    agendaOptions,
                    agendaModel,
                    itemObject,
                    newColView
                })
            ) {
                // Compute its new timestamps (we re use the magnet function because there might be some approximation when parsing width and adding it to the pxX)
                newProperties.startTs = magnet(pxToTimestampY(newColContext, { px: newProperties.pxY, targetDateType: WorkDateType.START }), itemObject, oldColData, agendaOptions.magnetismOptions || {}, { adjustOnlyStartTs: true });
                newProperties.finishTs = pxToTimestampY(newColContext, { px: newProperties.pxY + fromPx(htmlItem.style.height), targetDateType: WorkDateType.FINISH });
            }

            // Check permission on the item's rescheduling on the new timestamps
            if (!canItemBeDroppedOnColumn<ItemObject, ColumnObject>({
                agendaModel,
                agendaOptions,
                itemObject,
                oldColView,
                newColView,
                newStartTs: newProperties.startTs,
                newFinishTs: newProperties.finishTs
            })) {
                resetMoveDropItem(oldColView);
                return;
            }

            updatesToDo.setTimestamps = true;

            // Update the old colContent if item can d
            colContextsToUpdate.push(oldColContext);

            // If col has changed update content
            if (colHasChanged) {
                // Remove item's from original col
                agenda.removeItemFromColumnCache(colDataUid, itemObject, oldColContext.column);

                // Add item in the destination col
                agenda.addItemToColCache(newColUid, itemObject, newColContext.column);

                // Update item's colUids
                itemColumnUids = agendaModel.getItemColumnUids(itemObject);
                removeFirstFromArray(itemColumnUids, colDataUid);
                itemColumnUids.push(newColUid);

                updatesToDo.setItemColUids = true;

                colContextsToUpdate.push(newColContext);
            }

            let logMessage: string | undefined = agenda.options.logMessage?.formatMessage?.([itemObject], "move");
            if (logMessage == null) {
                logMessage = agenda.i18n.logMessage.moveAgendaItem({
                    items: formatItems([itemObject], agenda),
                    targetColumn: agenda.options.logMessage?.getColName && newColView.columnContext?.column ?
                        agenda.i18n.logMessage.onColumn + agenda.options.logMessage?.getColName(newColView.columnContext?.column) : "",
                    targetDate: agenda.i18n.logMessage.atDate + format(newProperties.startTs, DEFAULT_DATETIME_FORMAT)
                });
            }

            // Actions with data computed
            agendaModel.encapsulateInAction(async (action) => {
                let processMove = true;
                if (agendaModel.moveItem && itemColumnUids) {
                    const moveResult = await agendaModel.moveItem({ itemObject, updatesToDo, itemColumnUids, newProperties }, action);
                    if (moveResult !== "continue_with_standard_process") {
                        processMove = false;
                    }
                }
                if (processMove) {
                    if (updatesToDo.setItemColUids && itemColumnUids) {
                        agendaModel.setItemColumnUids(itemObject, itemColumnUids, action);
                    }
                    if (updatesToDo.setTimestamps) {
                        agendaModel.setItemStartTs(itemObject, newProperties.startTs, action, ItemInteraction.MOVE);
                        agendaModel.setItemFinishTs(itemObject, newProperties.finishTs, action, ItemInteraction.MOVE);
                    }

                    if (agendaModel.afterMoveItemProcess && itemColumnUids) {
                        await agendaModel.afterMoveItemProcess({ itemObject, itemColumnUids, action });
                    }
                }

                // Dispatch drop item event
                agenda.itemDropEvent.emit({
                    action,
                    itemTop: htmlItem.style.top,
                    itemHeight: htmlItem.style.height,
                    startTs: newProperties.startTs,
                    finishTs: newProperties.finishTs,
                    oldColUid: colDataUid,
                    newColUid: newColUid ?? colDataUid,// colDataUid is always defined here, use colDataUid as fallback
                    resetPositionX: !updatesToDo.setItemColUids,
                    resetPositionY: !updatesToDo.setTimestamps,
                    item: itemObject,
                    htmlItem,
                    originalMouseEvent: event,
                    tsDrop
                });

                return action.operations.length;
            }, {
                logMessage
            }).then((operationsCount) => {

                // remove drag style
                htmlItem.classList.remove(ITEM_DRAGGED_CSS_CLASS);

                // if app will refresh later, we don't refresh the agenda items
                if (!agendaOptions.appWillRefreshAgendaAfterAction || operationsCount === 0) {
                    // Update item
                    if (newProperties.hasBeenMoved) {
                        updateItemAndColumns(colContextsToUpdate, agendaContext, agenda, agendaModel, agendaOptions, DayAgendaRefreshReason.ITEM_MOVE);
                    }
                    // Reset style properties if X or Y new position are invalid
                    resetHtmlItemStyle(htmlItem, oldItemStyle, ["zIndex", "boxShadow", "cursor"]);
                }

                if (newProperties.hasBeenMoved) {
                    agenda.selectionManager.resetSelectedData();
                }

                // Dispatch item interaction finish event
                agenda.itemInteractionFinishEvent.emit({ event: ItemInteraction.MOVE });

            }).catch(showErrorInDialog).finally(() => {
                // Reset dragMove main direction
                moveDragEventMainDirection = null;
            });

            resetMoveDropItem(newColView);
        }

    };

    // event listener is removed after call
    document.body.addEventListener("mouseup", onMoveDropListenerObject, { once: true });
}

/** @returns the colView from the agenda cache and colViewHasChanged=true if it has changed */
function getUpToDateColView<
    ItemObject,
    ColumnObject
>(colView: CjsDayAgendaColumnView<ItemObject, ColumnObject>, agenda: CjsDayAgenda<ItemObject, ColumnObject>): { hasChanged: boolean, colView: CjsDayAgendaColumnView<ItemObject, ColumnObject> } {
    let hasChanged = false;
    if (colView.columnContext?.uid) {
        // get colView from cache in case there was a refresh in the meanwhile
        const uptoDateColView = agenda.columnViewsByUidCache[colView.columnContext?.uid];
        if (uptoDateColView && uptoDateColView !== colView) {
            hasChanged = true;
            colView = uptoDateColView;
        }
    }
    return { hasChanged, colView: colView };
}

let previousDialog: CjsDialog | undefined;

/** Bind tmp item creation drop event => ItemObject creation if needed */
function bindCreationClickEvent<
    ItemObject,
    ColumnObject
>(
    properties: PositionProperties,
    colView: CjsDayAgendaColumnView<ItemObject, ColumnObject>,
    event: MouseEvent,
    agenda: CjsDayAgenda<ItemObject, ColumnObject>,
    agendaModel: DayAgendaModel<ItemObject, ColumnObject>,
    agendaContext: DayAgendaContext,
    agendaOptions: DayAgendaOptions<ItemObject, ColumnObject>
): void {

    const colViewChange = getUpToDateColView(colView, agenda);
    colView = colViewChange.colView;
    if (!colView.columnContext?.column) { return; }

    // Compute new item's timestamps
    let newStartTs = magnet(pxToTimestampY(colView.columnContext, { px: properties.pxY, targetDateType: WorkDateType.START }), null, colView.columnContext?.column as ColumnObject, agendaOptions.magnetismOptions || {}, { adjustOnlyStartTs: true });
    let newFinishTs = magnet(pxToTimestampY(colView.columnContext, { px: properties.pxY + properties.height, targetDateType: WorkDateType.FINISH }), null, colView.columnContext?.column as ColumnObject, agendaOptions.magnetismOptions || {}, { adjustOnlyFinishTs: true });

    previousDialog?.dispose();

    const confirmCreationCb = (itemObjectToCreate: ItemObject): Promise<void> => {

        newStartTs = agendaModel.getItemStartTs(itemObjectToCreate);
        newFinishTs = agendaModel.getItemFinishTs(itemObjectToCreate);

        return agendaModel.encapsulateInAction((action) => {
            // Create item(s) if allowed
            if (colView.columnContext?.column && agendaModel.canCreateItem(colView.columnContext.column as ColumnObject, newStartTs, newFinishTs)) {
                return agendaModel.createItem(colView.columnContext.column as ColumnObject, newStartTs, newFinishTs, undefined, action).then((items) => {
                    let logMessage;
                    if (agenda.options.logMessage?.formatMessage) {
                        logMessage = agenda.options.logMessage.formatMessage(wrapAsArray(items), "create");
                    }
                    if (logMessage == null) {
                        logMessage = agenda.i18n.logMessage.createAgendaItem({
                            items: formatItems(wrapAsArray(items), agenda),
                            target: agenda.i18n.logMessage.atDate + format(newStartTs, DEFAULT_DATETIME_FORMAT)
                        });
                    }
                    action.params.logMessage = logMessage;
                    return items;
                });
            } else {
                return Promise.resolve([itemObjectToCreate]);
            }

        }, {
            logMessage: ""// defined above
        }).then((itemCreated: ItemObject | ItemObject[]) => {

            if (!(Array.isArray(itemCreated) && itemCreated.length === 0)) {
                const items = wrapAsArray(itemCreated);

                // if app will refresh later, we don't refresh the agenda items
                if (!agendaOptions.appWillRefreshAgendaAfterAction) {

                    // Update agenda cols impacted
                    updateAgendaColumns(items, agenda, agendaModel, agendaContext, agendaOptions, { action: "add" });
                    //  agenda.selectionManager.addToSelectedData({ item });

                    // Refresh agenda to display new cols or delete undisplayed cols 
                    agenda.refresh(DayAgendaRefreshReason.ITEM_CREATE);
                }

                // Dispatch create item event
                agenda.afterItemCreateEvent.emit({
                    items: itemCreated,
                    clickEvent: event
                });
            }

            // Dispatch item interaction finish event
            // (even if nothing was created - because in some application we don't bother telling the items we have created since we refresh the page after)
            agenda.itemInteractionFinishEvent.emit({ event: ItemInteraction.CREATE });

        }).catch(showErrorInDialog);
    };

    const createItemDialog = new CjsDayAgendaEditItemForm<ItemObject, ColumnObject>({
        agendaModel,
        column: colView.columnContext.column,
        isUpdate: false,
        item: {
            startTs: properties.startTs,
            finishTs: properties.finishTs
        } as ItemObject
    });


    previousDialog = new CjsDialog({
        title: agenda.i18n.editItemFormDialog.label.create,
        closeOnEscapeKey: true,
        contentScrollable: true,
        enableDismiss: true,
        enableDraggable: true,
        position: DialogPosition.CENTER,
        size: DialogSize.LARGE,
        preConfig: DialogPreConfig.ADD,
        onConfirmButtonClick: () => {
            return createItemDialog.getItemToCreate().then(itemsToCreate => {
                if (itemsToCreate == null) {
                    return Promise.reject({ preventClose: true });
                }
                return confirmCreationCb(itemsToCreate);
            });
        },
        onCloseButtonClick: () => {
            previousDialog = undefined;
            return Promise.resolve();
        }
    });
    previousDialog.setBody(createItemDialog);
}

/** Bind double click event on item */
function bindDoubleClickEvent<
    ItemObject,
    ColumnObject
>(
    itemObject: ItemObject,
    htmlItem: HTMLElement,
    colView: CjsDayAgendaColumnView<ItemObject, ColumnObject>,
    agenda: CjsDayAgenda<ItemObject, ColumnObject>,
    agendaModel: DayAgendaModel<ItemObject, ColumnObject>,
    agendaContext: DayAgendaContext,
    agendaOptions: DayAgendaOptions<ItemObject, ColumnObject>
): void {

    const column = colView.columnContext?.column;
    if (!column) { return; }

    const confirmUpdateCb = (itemObjectToUpdate: ItemObject): Promise<void> => {

        const newStartTs = agendaModel.getItemStartTs(itemObjectToUpdate);
        const newFinishTs = agendaModel.getItemFinishTs(itemObjectToUpdate);

        return agendaModel.encapsulateInAction((action) => {
            // Create item(s) if allowed
            if (colView.columnContext?.column) {
                return agendaModel.updateItem(itemObjectToUpdate, colView.columnContext.column as ColumnObject, newStartTs, newFinishTs, undefined, action).then((items) => {
                    let logMessage;
                    if (agenda.options.logMessage?.formatMessage) {
                        logMessage = agenda.options.logMessage.formatMessage(wrapAsArray(items), "move");
                    }
                    if (logMessage == null) {
                        logMessage = agenda.i18n.logMessage.createAgendaItem({
                            items: formatItems(wrapAsArray(items), agenda),
                            target: agenda.i18n.logMessage.atDate + format(newStartTs, DEFAULT_DATETIME_FORMAT)
                        });
                    }
                    action.params.logMessage = logMessage;
                    return items;
                });
            } else {
                return Promise.resolve([itemObjectToUpdate]);
            }

        }, {
            logMessage: ""// defined above
        }).then((itemUpdated: ItemObject | ItemObject[]) => {

            if (!(Array.isArray(itemUpdated) && itemUpdated.length === 0)) {
                const items = wrapAsArray(itemUpdated);

                // if app will refresh later, we don't refresh the agenda items
                if (!agendaOptions.appWillRefreshAgendaAfterAction) {

                    // Update agenda cols impacted
                    updateAgendaColumns(items, agenda, agendaModel, agendaContext, agendaOptions, { action: "update" });
                    for (const item of items) {
                        agenda.selectionManager.addToSelectedData({ item, htmlItem });
                    }

                    // Refresh agenda to display new cols or delete undisplayed cols 
                    agenda.refresh(DayAgendaRefreshReason.ITEM_CREATE);
                }

                // Dispatch create item event
                agenda.afterItemCreateEvent.emit({
                    items: itemUpdated,
                    clickEvent: event
                });
            }

            // Dispatch item interaction finish event
            // (even if nothing was created - because in some application we don't bother telling the items we have created since we refresh the page after)
            agenda.itemInteractionFinishEvent.emit({ event: ItemInteraction.CREATE });

        }).catch(showErrorInDialog);
    };

    const updateItemDialog = new CjsDayAgendaEditItemForm<ItemObject, ColumnObject>({
        agendaModel,
        column,
        isUpdate: true,
        item: itemObject
    });

    previousDialog = new CjsDialog({
        title: agenda.i18n.editItemFormDialog.label.update,
        closeOnEscapeKey: true,
        contentScrollable: true,
        enableDismiss: true,
        enableDraggable: true,
        position: DialogPosition.CENTER,
        size: DialogSize.LARGE,
        preConfig: DialogPreConfig.ADD,
        onConfirmButtonClick: () => {
            return updateItemDialog.getItemToCreate().then(itemsToUpdate => {
                if (itemsToUpdate == null) {
                    return Promise.reject({ preventClose: true });
                }
                return confirmUpdateCb(itemsToUpdate);
            });
        },
        onCloseButtonClick: () => {
            previousDialog = undefined;
            return Promise.resolve();
        }
    });
    previousDialog.setBody(updateItemDialog);

    previousDialog.confirmButton?.options.setAndRefreshIfChanged({ label: agenda.i18n.editItemFormDialog.confirm.update });
}


/** Highlight a hovered col if different and not buffer */
function highlightColumnIfNeeded<
    ItemObject,
    ColumnObject
>({
    agenda,
    hoverItem,
    htmlItem,
    agendaOptions,
    agendaModel,
    oldColView,
    itemObject,
    newStartTs,
    newFinishTs,
    event
}: {
    agenda: CjsDayAgenda<ItemObject, ColumnObject>,
    hoverItem: {
        columnHovered: CjsDayAgendaColumnView<ItemObject, ColumnObject>,
        oldColumnHoveredStyle: CSSStyleDeclaration
    },
    htmlItem: HTMLElement,
    agendaOptions: DayAgendaOptions<ItemObject, ColumnObject>,
    agendaModel: DayAgendaModel<ItemObject, ColumnObject>,
    oldColView: CjsDayAgendaColumnView<ItemObject, ColumnObject>,
    itemObject: ItemObject,
    newStartTs: UnixTimestamp,
    newFinishTs: UnixTimestamp,
    event: MouseEvent
}): void {
    // Retrieve HTML col element the item is dragged over
    const elementsMouseIsOver = document.elementsFromPoint(event.clientX, event.clientY);
    const newColView = elementsMouseIsOver.find((elem) => { return elem.classList.contains(CjsDayAgendaColumnView.TAG_NAME); }) as CjsDayAgendaColumnView<ItemObject, ColumnObject> | undefined;

    // Retrieve the newColView object
    const newColumnUid = newColView?.columnContext?.uid;
    if (!newColView || !newColumnUid || !hoverItem.columnHovered) {
        return;
    }

    // To be colored, the col must be a different one.
    oldColView.classList.remove(COLUMN_HOVERED_CLASS);

    hoverItem.columnHovered = newColView;
    removeColumnViewsHoverStyle(newColView, agenda);

    const hoverClass = [COLUMN_HOVERED_CLASS];
    if (!agenda.options.enableHover) {
        hoverClass.length = 0;// empty array
    }

    const canDropItemOnCol = canItemBeDroppedOnColumn({ agendaOptions, agendaModel, itemObject, newStartTs, newFinishTs, oldColView: oldColView, newColView: newColView });
    if (!canDropItemOnCol) {
        hoverClass.push(COLUMN_HOVERED_NOT_ALLOWED_CLASS);
        htmlItem.style.cursor = "not-allowed";
    } else {
        htmlItem.style.cursor = "default";
    }

    newColView.classList.add(...hoverClass);
}

/** Add or delete the items in the agenda cache */
export function updateAgendaColumnCache<
    ItemObject,
    ColumnObject
>(items: ItemObject[], agenda: CjsDayAgenda<ItemObject, ColumnObject>, agendaModel: DayAgendaModel<ItemObject, ColumnObject>, options: { action: "add" | "delete" }): void {
    updateAgendaColumns(items, agenda, agendaModel, agenda.getAgendaContext(), agenda.options, options);
}

/** 
 * Filter and regroup the agenda cols impacted by the items and update their context, 
 * Add or delete the items in the agenda cache 
 */
function updateAgendaColumns<
    ItemObject,
    ColumnObject
>(
    items: ItemObject[],
    agenda: CjsDayAgenda<ItemObject, ColumnObject>,
    agendaModel: DayAgendaModel<ItemObject, ColumnObject>,
    agendaContext: DayAgendaContext,
    agendaOptions: DayAgendaOptions<ItemObject, ColumnObject>,
    options: { action: "add" | "update" | "delete" }
): void {
    const colContextsToUpdate: ColumnContext<ItemObject, ColumnObject>[] = [];
    const colUidsToUpdate: string[] = [];

    // For each item : retrieve the impacted cols and update the agenda cache according to the operation (creation or deletion)
    for (const item of items) {
        const colUids = agendaModel.getItemColumnUids(item);
        for (const colUid of colUids) {
            // Retrieve each impacted col
            const tmpColView = agenda.columnViewsByUidCache[colUid];
            if (tmpColView && tmpColView.columnContext) {
                // Update the agenda item cache by adding or removing the item
                if (options.action === "delete") {
                    agenda.removeItemFromColumnCache(colUid, item, tmpColView.columnContext.column);
                    tmpColView.columnContext.itemContexts = [];// buildColContextItems (called in updateColContext below) uses current itemContexts so we need to clear it
                } else if (options.action === "add") {
                    agenda.addItemToColCache(colUid, item, tmpColView.columnContext.column);
                }
                // Add the col context to update if it isn't already
                if (!colUidsToUpdate.includes(colUid)) {
                    colContextsToUpdate.push(tmpColView.columnContext);
                    colUidsToUpdate.push(colUid);
                }
            }
        }
    }
    // Update the col context with the item modifications
    updateColContext(colContextsToUpdate, agendaContext, agenda, agendaModel, agendaOptions);
}


// #region DELETE Event

/** 
 * Bind item deletion event on DELETE key down
 * Will delete all the item selected in the Selection Manager of the agenda then update the col contexts and refresh the agenda.
 */
function bindDeleteItemEvent<
    ItemObject,
    ColumnObject
>(
    agenda: CjsDayAgenda<ItemObject, ColumnObject>,
    agendaModel: DayAgendaModel<ItemObject, ColumnObject>,
    agendaContext: DayAgendaContext,
    agendaOptions: DayAgendaOptions<ItemObject, ColumnObject>
): void {

    document.addEventListener("keydown", (ev) => {
        if (ev.key !== KeyboardInput.DELETE) { return; }
        if (!agenda.isMouseInsideColumnsContainer()) {
            return;
        }
        // Proceed if the key down is Delete
        const selectedData = agenda.selectionManager.getSelectedData();
        if (selectedData.size === 0) { return; }
        const itemsToDelete: ItemObject[] = [];

        // Check if delete can be performed
        let canDeleteItem = true;
        selectedData.forEach((sData) => {
            const canDeleteData = agendaModel.canDeleteItem(sData.item);
            if (canDeleteData) {
                itemsToDelete.push(sData.item);
            }
            canDeleteItem = canDeleteItem && canDeleteData;
        });
        if (!canDeleteItem || itemsToDelete.length === 0) { return; }

        // Init and add delete promises for the selected items
        // Execute the promises then update and refresh the agenda. Reset also the selection
        agendaModel.encapsulateInAction((action) => {
            return agendaModel.deleteItem(itemsToDelete, action);
        }, {
            logMessage: agenda.options.logMessage?.formatMessage?.(itemsToDelete, "delete") ??
                agenda.i18n.logMessage.deleteAgendaItem({ items: formatItems(itemsToDelete, agenda) })
        }).then((items: ItemObject | ItemObject[]) => {

            // Flatten the array of deleted items
            const itemsFlat: ItemObject[] = wrapAsArray(items);

            // if app will refresh later, we don't refresh the agenda items
            if (!agendaOptions.appWillRefreshAgendaAfterAction) {
                // Update agenda cols contexts if needed
                updateAgendaColumns(itemsFlat, agenda, agendaModel, agendaContext, agendaOptions, { action: "delete" });

                // Refresh agenda to display new cols or delete undisplayed cols 
                agenda.refresh(DayAgendaRefreshReason.ITEM_DELETE);
            }

            // Reset item selection
            const removedData = agenda.selectionManager.resetSelectedData();

            // Dispatch on after item remove event
            agenda.afterItemRemoveEvent.emit({ removedData });

            // Dispatch item interaction finish event
            agenda.itemInteractionFinishEvent.emit({ event: ItemInteraction.DELETE });

        }).catch(showErrorInDialog);

    });
}
// #endregion

/** Return true if drag in X is enable */
function isDragXEnable<
    ItemObject,
    ColumnObject
>(p: {
    agendaOptions: DayAgendaOptions<ItemObject, ColumnObject>,
    agendaModel: DayAgendaModel<ItemObject, ColumnObject>,
    itemObject: ItemObject,
    newColView: CjsDayAgendaColumnView<ItemObject, ColumnObject>
}): boolean {

    return true;
    /*

    const canChangeColumn = p.agendaModel?.canChangeColumn?.(p.itemObject, p.newColView.columnContext?.column as ColumnObject) ?? true;
    return (p.agendaOptions.dragMode === "x" ||
        p.agendaOptions.dragMode === "both" ||
        moveDragEventMainDirection === "x" ||
        moveDragEventMainDirection !== "y" &&
        isDragOneAxisEnable(p.agendaOptions)
    ) && canChangeColumn;*/
}

/** Return true if drag in Y is enable */
function isDragYEnable<
    ItemObject,
    ColumnObject
>(p: {
    agendaOptions: DayAgendaOptions<ItemObject, ColumnObject>,
    agendaModel: DayAgendaModel<ItemObject, ColumnObject>,
    itemObject: ItemObject,
    newStartTs: UnixTimestamp,
    newFinishTs: UnixTimestamp
}): boolean {

    const canRescheduleItem = p.agendaModel?.canRescheduleItem?.(p.itemObject, p.newStartTs, p.newFinishTs) ?? true;
    return (p.agendaOptions.dragMode === "y" ||
        p.agendaOptions.dragMode === "both" ||
        moveDragEventMainDirection === "y" ||
        moveDragEventMainDirection !== "x" &&
        isDragOneAxisEnable(p.agendaOptions)
    ) && canRescheduleItem;
}

/** Return true if drag on one axis at same time is enable */
function isDragOneAxisEnable<
    ItemObject,
    ColumnObject
>(agendaOptions: DayAgendaOptions<ItemObject, ColumnObject>): boolean {
    return agendaOptions.dragMode === "oneAxis";
}

/** Return true if item can be dropped into col */
function canItemBeDroppedOnColumn<
    ItemObject,
    ColumnObject
>(p: {
    agendaOptions: DayAgendaOptions<ItemObject, ColumnObject>,
    agendaModel: DayAgendaModel<ItemObject, ColumnObject>,
    itemObject: ItemObject,
    oldColView: CjsDayAgendaColumnView<ItemObject, ColumnObject>,
    newColView: CjsDayAgendaColumnView<ItemObject, ColumnObject>,
    newStartTs: UnixTimestamp,
    newFinishTs: UnixTimestamp
}): boolean {
    const oldColumn = p.oldColView.columnContext?.column;
    if (!oldColumn) { return false; }

    const newColumn = p.newColView.columnContext?.column;
    if (!newColumn) { return false; }

    const columnHasChanged = p.oldColView.columnContext?.uid !== p.newColView.columnContext?.uid;

    if (!isDragXEnable<ItemObject, ColumnObject>({
        agendaOptions: p.agendaOptions,
        agendaModel: p.agendaModel,
        itemObject: p.itemObject,
        newColView: p.newColView
    }) && columnHasChanged) {
        return false;
    }

    return p.agendaModel.canDropItemOnColumn(p.itemObject, p.newStartTs, p.newFinishTs, oldColumn, newColumn);
}